import './TagsInput.css';
import './../input.scss';
import React, { useState } from 'react';
import { IconName } from '../../Icon/IconName';
import { Icon } from '../../Icon/Icon';
import classnames from 'classnames';
import { Shortcut } from '../../Shortcut/Shortcut';
import { useForm } from '../Form';
import { t } from '../../../locales/translate';
import { TagsGroup } from '../../Tag/TagsGroup';

interface Props {
  name: string;
  icon?: IconName;
  label?: string;
  required?: boolean;
  actions?: React.ReactNode | React.ReactNodeArray;
  description?: string;
  disabled?: boolean;
  onTagDelete?: (tag: string) => void;
}

export function TagsInput({
  name,
  icon,
  label,
  required,
  actions,
  description,
  disabled,
  onTagDelete,
}: Props): JSX.Element {
  const [tags, setTags] = useForm<string[]>(name);
  const [active, setActive] = useState(false);
  const [pendingTag, setPendingTag] = useState(' ');

  function addTag(): void {
    const tag = pendingTag.trim();
    if (!tag) {
      return;
    }
    if (tags?.length) {
      const exists = tags.includes(tag);
      if (!exists) {
        setTags([...tags, tag]);
      }
    } else {
      setTags([tag]);
    }
    setPendingTag(' ');
  }

  function removeLastTag(): void {
    if (tags && !pendingTag) {
      if (onTagDelete) {
        onTagDelete(tags[tags.length - 1]);
      }

      setTags([...tags.filter((_, i) => i !== tags.length - 1)]);
    }
  }

  function removeTagAtIndex(index: number): void {
    if (!tags) {
      return;
    }

    if (onTagDelete) {
      onTagDelete(tags[index]);
    }
    setTags([...tags.filter((_, i) => i !== index)]);
  }

  return (
    <div className={classnames('input tags-input', { 'input--with-icon': icon })}>
      {icon && <Icon name={icon} className="input__icon" />}
      <div
        className={classnames('input__container', {
          'input__container--active': active,
          'input__container--full': active || (tags && tags.length > 0),
        })}
      >
        <div className="input__input-wrapper">
          {label && (
            <label className="input__label" htmlFor={name}>
              {label}
              {!required && ` (${t.general.optional})`}
            </label>
          )}
          <TagsGroup
            tags={tags?.map((tag, index) => ({
              id: index.toString(),
              label: tag,
            }))}
            onRemove={removeTagAtIndex}
            input={
              <input
                className="input__input"
                type="text"
                disabled={disabled}
                onChange={(e) => {
                  setPendingTag(e.currentTarget.value);
                }}
                value={pendingTag}
                onFocus={() => {
                  setActive(true);
                }}
                onBlur={() => {
                  addTag();
                  setActive(false);
                }}
              />
            }
            expandHiddenTags="bottom-end"
            onClear={() => {
              setTags([]);
            }}
          />
          {actions && <div className="input__actions">{actions}</div>}
        </div>
        <div className="input__description-wrapper">
          {description && <div className="input__description">{description}</div>}
        </div>
      </div>
      <Shortcut keys="Enter" onInvoke={addTag} overrideInput={active} />
      <Shortcut keys=" " onInvoke={addTag} overrideInput={active} />
      <Shortcut keys="Backspace" onInvoke={removeLastTag} overrideInput={active} />
    </div>
  );
}
